import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatTRY, downloadExport, toNum } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PRESETS } from "@/components/DateRangeFilter";
import { FileSpreadsheet, ArrowUpRight, ArrowDownRight, Receipt, Package, Pencil, Trash2, ChevronDown, ChevronRight, ExternalLink, Check, X } from "lucide-react";
import { toast } from "sonner";

const ICONS = { sale: Receipt, income: ArrowUpRight, expense: ArrowDownRight, cogs: Package, retail: Package };
const POSITIVE = new Set(["sale", "income", "retail"]);
const QUICK = [["today", "Bugün"], ["week", "Son 7 Gün"], ["month", "Bu Ay"], ["all", "Tümü"]];

const groupRows = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const g = map.get(r.group) || { name: r.group, rows: [], total: 0 };
    g.rows.push(r); g.total += r.amount; map.set(r.group, g);
  }
  return [...map.values()].sort((a, b) => b.total - a.total);
};

export function FinanceDetailDialog({ detail, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [collapsed, setCollapsed] = useState({});
  const [editing, setEditing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (detail) { setRange({ start: detail.start || "", end: detail.end || "" }); setCollapsed({}); setEditing(null); setDirty(false); }
  }, [detail]);

  const params = useMemo(() => {
    if (!detail) return null;
    const p = { kind: detail.kind };
    if (range.start) p.start = range.start;
    if (range.end) p.end = range.end;
    return p;
  }, [detail, range]);

  const load = () => params && api.get("/finance/details", { params }).then((r) => setData(r.data)).catch(() => toast.error("Detaylar yüklenemedi"));
  useEffect(() => { setData(null); load(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => { if (dirty) onChanged?.(); onClose(); };

  const exportXlsx = async () => {
    try { await downloadExport("/export/finance-details.xlsx", params, `fithouse_${detail.kind}_detay.xlsx`); toast.success("Excel dosyası indirildi"); }
    catch { toast.error("Dışa aktarma başarısız"); }
  };

  const del = async (r) => {
    if (!window.confirm(`"${r.label}" kaydı silinsin mi?`)) return;
    try { await api.delete(`/transactions/${r.id}`); toast.success("Kayıt silindi"); setDirty(true); load(); }
    catch (e) { toast.error(e.response?.status === 403 ? "Gelir/Gider düzenleme yetkiniz yok" : "Silinemedi"); }
  };

  const saveEdit = async () => {
    const amount = toNum(editing.amount);
    if (!(amount > 0)) return toast.error("Geçerli bir tutar girin");
    if (!editing.category.trim()) return toast.error("Kategori boş olamaz");
    try {
      await api.put(`/transactions/${editing.id}`, { type: editing.kind, category: editing.category.trim(), amount, description: editing.sub, date: editing.date });
      toast.success("Kayıt güncellendi"); setEditing(null); setDirty(true); load();
    } catch (e) { toast.error(e.response?.status === 403 ? "Gelir/Gider düzenleme yetkiniz yok" : "Güncellenemedi"); }
  };

  const groups = data ? groupRows(data.rows) : [];
  const grouped = groups.length > 1;
  const periodLabel = range.start || range.end ? `${range.start || "Başlangıç"} → ${range.end || "Bugün"}` : "Tüm zamanlar";

  const Row = ({ r }) => {
    const Icon = ICONS[r.kind] || Receipt;
    const pos = POSITIVE.has(r.kind);
    if (editing?.id === r.id) {
      return (
        <div className="grid grid-cols-12 items-end gap-2 py-2.5" data-testid={`finance-detail-edit-${r.id}`}>
          <Input className="col-span-12 h-8 text-sm sm:col-span-4" value={editing.category} data-testid="finance-edit-category" onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Kategori" />
          <Input className="col-span-5 h-8 text-sm sm:col-span-2" inputMode="decimal" value={editing.amount} data-testid="finance-edit-amount" onChange={(e) => setEditing({ ...editing, amount: e.target.value })} placeholder="Tutar" />
          <Input className="col-span-7 h-8 text-sm sm:col-span-3" type="date" value={editing.date} data-testid="finance-edit-date" onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
          <Input className="col-span-12 h-8 text-sm sm:col-span-2" value={editing.sub} data-testid="finance-edit-desc" onChange={(e) => setEditing({ ...editing, sub: e.target.value })} placeholder="Açıklama" />
          <div className="col-span-12 flex justify-end gap-1 sm:col-span-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="finance-edit-save" onClick={saveEdit}><Check className="h-4 w-4 text-primary" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="finance-edit-cancel" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
          </div>
        </div>
      );
    }
    return (
      <div className="group flex items-center justify-between gap-3 py-2.5" data-testid={`finance-detail-row-${r.id}`}>
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${pos ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}><Icon className="h-4 w-4" /></div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{r.label}</p>
            <p className="truncate text-xs text-muted-foreground">{[r.date, r.time, r.sub].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className={`font-heading font-bold ${pos ? "text-primary" : "text-destructive"}`}>{pos ? "+" : "−"}{formatTRY(r.amount)}</span>
          {r.editable && (
            <>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`finance-row-edit-${r.id}`} onClick={() => setEditing({ ...r, amount: String(r.amount) })}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`finance-row-delete-${r.id}`} onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </>
          )}
          {r.kind === "sale" && (
            <Button size="icon" variant="ghost" className="h-7 w-7" title="Satış Geçmişi'nde aç" data-testid={`finance-row-open-sale-${r.id}`} onClick={() => { close(); navigate(`/satis-gecmisi?sale=${r.id}`); }}><ExternalLink className="h-3.5 w-3.5" /></Button>
          )}
          {r.kind === "retail" && (
            <Button size="icon" variant="ghost" className="h-7 w-7" title="Perakende sayfasında düzenle" data-testid={`finance-row-open-retail-${r.id}`} onClick={() => { close(); navigate(`/perakende?sale=${r.id}`); }}><ExternalLink className="h-3.5 w-3.5" /></Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] sm:max-w-3xl" data-testid="finance-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span data-testid="finance-detail-title">{detail?.title}</span>
            <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="finance-detail-export" disabled={!data || data.rows.length === 0}>
              <FileSpreadsheet className="mr-1 h-4 w-4 text-primary" /> Excel
            </Button>
          </DialogTitle>
          <p className="text-xs text-muted-foreground" data-testid="finance-detail-period">{periodLabel}{data ? ` · ${data.count} kayıt` : ""}</p>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-secondary/40 p-2" data-testid="finance-detail-range">
          <Input type="date" className="h-8 w-36 text-xs" value={range.start} max={range.end || undefined} data-testid="finance-range-start" onChange={(e) => setRange({ ...range, start: e.target.value })} />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" className="h-8 w-36 text-xs" value={range.end} min={range.start || undefined} data-testid="finance-range-end" onChange={(e) => setRange({ ...range, end: e.target.value })} />
          <div className="ml-auto flex gap-1">
            {QUICK.map(([k, l]) => <Button key={k} size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`finance-range-${k}`} onClick={() => setRange(PRESETS[k]())}>{l}</Button>)}
          </div>
        </div>

        {!data ? <p className="py-8 text-center text-sm text-muted-foreground">Yükleniyor…</p> : (
          <div className="-mx-2 max-h-[50vh] overflow-y-auto px-2" data-testid="finance-detail-rows">
            {data.rows.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="finance-detail-empty">Bu dönemde kayıt yok</p>}
            {grouped ? groups.map((g) => {
              const open = !collapsed[g.name];
              const pct = data.total ? (g.total / data.total) * 100 : 0;
              return (
                <div key={g.name} className="mb-2 rounded-lg border border-border" data-testid={`finance-group-${g.name}`}>
                  <button type="button" className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-secondary/40" data-testid={`finance-group-toggle-${g.name}`} onClick={() => setCollapsed({ ...collapsed, [g.name]: open })}>
                    <span className="flex items-center gap-2 text-sm font-semibold">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{g.name}<span className="text-xs font-normal text-muted-foreground">{g.rows.length} kayıt</span></span>
                    <span className="flex items-center gap-3"><span className="text-xs text-muted-foreground">%{pct.toFixed(1)}</span><span className="font-heading font-bold" data-testid={`finance-group-total-${g.name}`}>{formatTRY(g.total)}</span></span>
                  </button>
                  <div className="h-1 bg-secondary"><div className="h-1 bg-primary/70" style={{ width: `${pct}%` }} /></div>
                  {open && <div className="divide-y divide-border px-3">{g.rows.map((r) => <Row key={r.id} r={r} />)}</div>}
                </div>
              );
            }) : <div className="divide-y divide-border">{data.rows.map((r) => <Row key={r.id} r={r} />)}</div>}
          </div>
        )}
        {data && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{data.net ? "Net (gelir − gider)" : "Toplam"}</span>
            <span className={`font-heading text-xl font-extrabold ${data.net && data.total < 0 ? "text-destructive" : ""}`} data-testid="finance-detail-total">{formatTRY(data.total)}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
